import React, { useState, useEffect } from 'react';
import './ScorecardOCRModal.css';
import ScoreInputPad from '../ScoreInputPad';

const ScorecardOCRModal = ({ isOpen, onClose, extractedData, roundPlayers, onConfirm }) => {
    const [mappings, setMappings] = useState({});
    const [editableScores, setEditableScores] = useState([]);
    const [activeEditingCell, setActiveEditingCell] = useState(null);

    // Initialize mappings and editable scores
    useEffect(() => {
        if (extractedData && extractedData.players) {
            // Mappings logic
            const initialMappings = {};
            extractedData.players.forEach((extractedPlayer, index) => {
                const match = roundPlayers.find(p => p.name.toLowerCase() === extractedPlayer.original_name.toLowerCase());
                if (match) {
                    initialMappings[index] = match.id;
                } else {
                    initialMappings[index] = "";
                }
            });
            setMappings(initialMappings);

            // Deep copy scores to editable state
            setEditableScores(JSON.parse(JSON.stringify(extractedData.players)));
        }
    }, [extractedData, roundPlayers, isOpen]); // Added isOpen to re-init if needed

    if (!isOpen || !extractedData) return null;

    const handlePlayerSelect = (extractedIndex, roundPlayerId) => {
        setMappings(prev => ({
            ...prev,
            [extractedIndex]: parseInt(roundPlayerId) || ""
        }));
    };

    const handleScoreClick = (playerIndex, holeNum, playerName) => {
        setActiveEditingCell({ playerIndex, holeNum, playerName });
    };

    const handleKeypadScoreSelect = (score) => {
        if (!activeEditingCell) return;

        const { playerIndex, holeNum } = activeEditingCell;

        setEditableScores(prevScores => {
            const newScores = [...prevScores];

            // Ensure the scores object exists
            if (!newScores[playerIndex].scores) newScores[playerIndex].scores = {};

            if (score === 'NS') {
                // For OCR review, we might just want to set null or a high score.
                // Keeping it consistent with common usage: null or 0.
                newScores[playerIndex].scores[holeNum] = null;
            } else {
                newScores[playerIndex].scores[holeNum] = score;
            }
            return newScores;
        });

        setActiveEditingCell(null);
    };

    const handleConfirm = () => {
        const mappedScores = [];
        editableScores.forEach((extractedPlayer, index) => {
            const assignedPlayerId = mappings[index];
            if (assignedPlayerId) {
                mappedScores.push({
                    playerId: assignedPlayerId,
                    scores: extractedPlayer.scores
                });
            }
        });
        onConfirm(mappedScores);
    };

    return (
        <div className="ocr-modal-overlay">
            <div className="ocr-modal-content">
                <h2>Review Scanned Scores</h2>
                <p style={{marginBottom: '15px', fontSize: '0.9rem', color: '#666'}}>
                    Match the scanned names to the players in this round and correct any scores if needed.
                </p>

                <div className="ocr-players-list">
                    {editableScores.map((player, index) => (
                        <div key={index} className="ocr-player-row">
                            <div className="ocr-player-header">
                                <span className="ocr-original-name">
                                    Scanned: "{player.original_name}"
                                </span>
                                <div className="ocr-match-select">
                                    <select
                                        value={mappings[index] || ""}
                                        onChange={(e) => handlePlayerSelect(index, e.target.value)}       
                                    >
                                        <option value="">Select Player</option>
                                        {roundPlayers.map(rp => (
                                            <option key={rp.id} value={rp.id}>{rp.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="ocr-scores-preview">
                                {Array.from({ length: 18 }, (_, i) => {
                                    const holeNum = i + 1;
                                    const score = player.scores[holeNum];
                                    return (
                                        <div
                                            key={holeNum}
                                            className={`ocr-score-item ${score ? 'has-score' : ''} clickable`}
                                            onClick={() => handleScoreClick(index, holeNum, player.original_name)}
                                        >
                                            <span className="ocr-hole-num">{holeNum}</span>
                                            <span className="ocr-score-val">{score || '-'}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="ocr-actions">
                    <button onClick={onClose} className="btn-secondary">Cancel</button>
                    <button onClick={handleConfirm} className="btn-primary">Apply Scores</button>
                </div>
            </div>

            {activeEditingCell && (
                <ScoreInputPad
                    playerName={activeEditingCell.playerName}
                    holeNumber={activeEditingCell.holeNum}
                    onScoreSelect={handleKeypadScoreSelect}
                    onClose={() => setActiveEditingCell(null)}
                />
            )}
        </div>
    );
};

export default ScorecardOCRModal;